import React, { useState, useEffect } from "react";
import Footer from "./Footer.jsx";
import {
  formatLongCountdown,
  ReplayGuessForm,
  ReplayStatsPanel,
  ReplayLastEntriesPanel,
  ReplaySpeedControls,
  ReplayElapsed,
  buildEventTimeline,
  countUsersEntered,
  useReplayPlayback,
} from "./replayShared.jsx";

const API_BASE = ""; // same-origin — nginx proxies /api and /ws to the backend

// The player components, the event-timeline builder and the playback
// loop itself now live in replayShared.jsx, shared with the admin
// Competition History modal — moved verbatim, not rewritten. What
// stayed here is what only this page has: the between-competition gap
// screen (a demo round is 5 competitions played in a loop; the admin
// modal plays one and stops) and the email-capture form that goes with
// it.

// A visual-only stand-in for App.jsx's own AnonymousReminderForm — this
// is a replay, not a real site visit, so there's no real backend to
// submit to. Exists purely to show a prospective partner that this
// marketing/re-engagement mechanism is real and built in, not just the
// core buy-pixels mechanic.
function ReplayReminderForm() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  if (submitted) {
    return <p style={{ opacity: 0.8, marginBottom: 10 }}>✓ We'll email you when the next competition is about to start.</p>;
  }
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setSubmitted(true);
      }}
      style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}
    >
      <input
        type="email"
        required
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        style={{ padding: "8px 10px", fontSize: 14 }}
      />
      <button type="submit">Notify me</button>
    </form>
  );
}

// ONE PLAYER, TWO SOURCES.
//
// It was written for the public demo link — a token in the path, no
// login. A buyer watching back a competition they played in needs the
// same playback against a different URL and an auth header, so the only
// two things that differed are now props.
//
// Default to the demo-link behaviour when only a token is given, so the
// public route is unchanged.
export default function DemoCompsReplayPage({ token, loadData, imageUrlFor, backHref }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // 1 — the pace the competition actually ran at. See SPEEDS in
  // replayShared for what that does and does not mean.
  const [speed, setSpeed] = useState(1);
  const [compIndex, setCompIndex] = useState(0);
  const [gapRemainingMs, setGapRemainingMs] = useState(0);

  useEffect(() => {
    (loadData
      ? loadData()
      : fetch(`${API_BASE}/api/demo-comps/${token}`).then((res) =>
          res.json().then((body) => ({ ok: res.ok, body }))
        ))
      .then(({ ok, body }) => {
        if (!ok) {
          setError(body.error || "Could not load this replay.");
          return;
        }
        setData(body);
      })
      .catch(() => setError("Could not reach the server."));
  }, [token]);

  const comp = data?.competitions?.[compIndex];
  const events = comp ? buildEventTimeline(comp) : [];

  const { phase, purchaseCount, guessCount, isLottery, speedRef, playing, clockRef } = useReplayPlayback(comp, events, speed);

  // The gap/hold after a competition ends — mirrors the real site's
  // own "ended" page mode, which shows the revealed image, winner
  // info, countdown, and email-capture form all together on one
  // screen. Plays the real recorded startDelayMs out naturally at the
  // chosen speed (confirmed fine to do, rather than needing special
  // handling) as the hold duration itself, then advances to the next
  // competition, looping back to the first once all 5 have shown.
  //
  // Deliberately a single effect using a local (non-state) `remaining`
  // variable to actually drive the countdown and decide when to
  // advance, rather than reading gapRemainingMs state for that
  // decision — a real bug this replaced: gapRemainingMs starts at its
  // initial useState value (0), and a separate effect that only set it
  // to the real duration could still see that stale 0 on the very same
  // commit, since one effect's state update isn't visible to a sibling
  // effect until a subsequent render — causing an immediate,
  // unintended advance before the gap screen ever genuinely showed,
  // which is what made competitions look like they were being skipped.
  useEffect(() => {
    if (!comp || phase !== "revealed") return;
    const initialMs = comp.startDelayMs || 60000;
    setGapRemainingMs(initialMs);
    let remaining = initialMs;
    function tick() {
      remaining -= 1000;
      if (remaining <= 0) {
        setCompIndex((i) => (i + 1) % (data?.competitions?.length || 1));
        return;
      }
      setGapRemainingMs(remaining);
      timeoutId = setTimeout(tick, 1000 / speedRef.current);
    }
    let timeoutId = setTimeout(tick, 1000 / speedRef.current);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, comp?.id]);

  if (error) {
    return (
      <div style={{ color: "var(--text-primary)", minHeight: "100vh", padding: 20, textAlign: "center" }}>
        <p style={{ opacity: 0.8, marginTop: 60 }}>{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ color: "var(--text-primary)", minHeight: "100vh", padding: 20 }}>
        <p style={{ opacity: 0.7, marginTop: 60, textAlign: "center" }}>Loading…</p>
      </div>
    );
  }
  // A loaded response with nothing in it. The backend now answers 410
  // for the one case that actually produced this (a pre-snapshot link
  // whose competitions have since been removed), so this shouldn't be
  // reachable — but it stays as a guard rather than being left to the
  // "did this load?" check above, which passed happily and let every
  // line below dereference an undefined competition. That crashed the
  // whole page to a blank screen, which is a far worse failure than the
  // dead link it was reporting.
  if (!data.competitions?.length) {
    return (
      <div style={{ color: "var(--text-primary)", minHeight: "100vh", padding: 20, textAlign: "center" }}>
        <p style={{ opacity: 0.8, marginTop: 60 }}>This replay is no longer available.</p>
      </div>
    );
  }

  const nextComp = data.competitions[(compIndex + 1) % data.competitions.length];
  // Server-composited, the exact same way the real site's own
  // /api/competitions/:id/public-image already works — the base fog
  // image plus every region purchased so far, re-fetched fresh on
  // every change.
  //
  // Both frames now come from the link's own token-scoped route,
  // including the revealed one. That used to point at
  // /api/competitions/:id/public-image, which reads the live
  // competition — fine while the competitions still existed, but the
  // whole point of replay snapshots is that a link outlives them, and
  // that route 404s the moment they're deleted. The token route serves
  // the frozen copy instead.
  const imageSrc = imageUrlFor
    ? imageUrlFor(comp.id, phase === "revealed" ? { revealed: true } : { regionCount: purchaseCount })
    : phase === "revealed"
      ? `${API_BASE}/api/demo-comps/${token}/competitions/${comp.id}/image?revealed=1`
      : `${API_BASE}/api/demo-comps/${token}/competitions/${comp.id}/image?regionCount=${purchaseCount}`;

  // comp.regions/comp.guesses are already sorted chronologically (see
  // the backend's own buildCompetitionReplayPayload comment), so
  // slicing to the counts tracked by the playback loop gives the
  // played-so-far list directly — no separate state needed for either.
  const playedRegions = comp.regions.slice(0, purchaseCount);
  const playedGuesses = comp.guesses.slice(0, guessCount);
  const usersEntered = countUsersEntered(playedRegions, playedGuesses);
  // Matches contentWidth's own formula on the real homepage — sidebar
  // width (260) plus the gap (16) between it and the image.
  const contentWidth = comp.imageWidth + 276;

  return (
    <div style={{ color: "var(--text-primary)", minHeight: "100vh" }}>
      <div style={{ maxWidth: contentWidth, margin: "0 auto", padding: "24px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 12, opacity: 0.6 }}>{data.siteName} — Demo replay</div>
            <h2 style={{ margin: "2px 0 0" }}>{phase === "playing" ? comp.title : `Competition ${compIndex + 1} of ${data.competitions.length}`}</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* WHERE IN THE COMPETITION THIS IS (1.515.0), the same clock
                the admin player shows. This page has no count bar to put
                it beside — it is a demo, not a control panel — so it sits
                with the speed buttons that drive it. It renders nothing
                at all for a snapshot recorded before both times were
                stored. */}
            {comp && (
              <span style={{ fontSize: 12, opacity: 0.6 }}>
                <ReplayElapsed
                  clockRef={clockRef}
                  startedAt={comp.actualStartAt}
                  endedAt={comp.answeredAt}
                  speedRef={speedRef}
                  playing={playing}
                  phase={phase}
                />
              </span>
            )}
            <ReplaySpeedControls speed={speed} setSpeed={setSpeed} />
          </div>
        </div>

        {phase === "revealed" && (
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ marginTop: 0, marginBottom: 4 }}>
              {comp.title} — {comp.wonByLottery ? "Lottery Drawn!" : "Solved!"}
            </h3>
            <p style={{ opacity: 0.8 }}>
              The answer was <strong>{comp.answer}</strong>
              {comp.wonByLottery ? ", resolved by a random draw among purchased pixels." : "."}
            </p>
            {data.reminderShowCountdown && (
              <p style={{ opacity: 0.8 }}>
                Next competition{nextComp?.title ? ` ("${nextComp.title}")` : ""} starts in{" "}
                <strong>{formatLongCountdown(gapRemainingMs)}</strong>
              </p>
            )}
            {data.reminderCaptureEmail && <ReplayReminderForm />}
          </div>
        )}

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ flex: "1 1 400px", minWidth: 280 }}>
            <div style={{ position: "relative", width: "100%", borderRadius: 8, overflow: "hidden", border: "1px solid var(--border-color)" }}>
              <img src={imageSrc} alt={comp.title} style={{ width: "100%", display: "block" }} />
            </div>

            {phase === "playing" && (
              <ReplayGuessForm
                answerMode={comp.answerMode}
                creditsPerEntry={comp.creditsPerEntry}
                creditsPerMajorUnitOnPixels={data.creditsPerMajorUnitOnPixels}
                currencySymbol={data.currencySymbol}
                isLottery={isLottery}
              />
            )}
          </div>

          {phase === "playing" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%", maxWidth: 260 }}>
              <ReplayStatsPanel imagesRevealed={purchaseCount} usersEntered={usersEntered} entriesSubmitted={guessCount} />
              <ReplayLastEntriesPanel guesses={playedGuesses} />
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
